import { ChangeEvent, ReactElement } from 'react';

import { TextFieldProps } from '@mui/material';
import {
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
} from 'react-hook-form';
import { NumericFormatProps } from 'react-number-format';

import { CustomNumericFormat } from '../CustomNumericFormat';

export type NumberFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  ControllerProps: Omit<ControllerProps<TFieldValues, TName>, 'render'>;
} & Omit<NumericFormatProps, 'customInput' | 'onValueChange'> &
  Omit<TextFieldProps, 'InputProps'> & {
    InputProps?: Omit<TextFieldProps['InputProps'], 'inputRef'>;
  };

export const NumberField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ControllerProps,
  ...props
}: NumberFieldProps<TFieldValues, TName>): ReactElement => {
  return (
    <Controller
      {...ControllerProps}
      render={({
        field: { ref, onChange, ...field },
        fieldState: { error },
      }) => (
        <CustomNumericFormat
          {...field}
          {...props}
          error={props.error || Boolean(error)}
          helperText={error?.message || props.helperText}
          InputProps={{
            ...props.InputProps,
            inputRef: ref,
            readOnly: props.readOnly,
          }}
          onValueChange={({ floatValue }, { event }) => {
            const valueToSave =
              typeof floatValue === 'undefined' ? null : floatValue;
            onChange(valueToSave);
            props.onChange &&
              props.onChange(event as ChangeEvent<HTMLInputElement>);
          }}
        />
      )}
    />
  );
};
